/* Scan to poster (not in the handoff — kit components). Scans on the school computer's scanner through
 * Brightivity Helper (Windows, 0.1.7+ sends the scan up), then AI Enhance / Print / Edit in Design / Save /
 * Download — StudioCore desktop ScanToPrint parity (m11 #6): scanner cards with the Epson pictures, re-detect,
 * USB guidance + driver install on the computer, AI Enhance (brightivity.dev/design/enhance, charged only when it
 * fully works), reset to original, richer library metadata. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TopBar } from '../app/AppShell.jsx';
import { useWallet } from '../app/wallet.jsx';
import { API_BASE } from '../config.js';
import { getSession } from '../api/client.js';
import { getPricing } from '../api/wallet.js';
import { runCommand } from '../api/bridge.js';
import { makeThumbnail, saveDesign } from '../api/library.js';
import { useDevices, useLocalComputer } from '../features/computers/computers.jsx';
import { handToPrint } from '../features/print/handoff.js';
import { usePrintStore } from '../features/print/PrintStore.jsx';
import { Button, Card, EmptyState, Icon, IconTile, Select, Spinner, Tag } from '../ui/index.jsx';
import '../features/scan/scan.css';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/index.js';

const atLeast = (v, min) => { const a = String(v || '0').split('.').map(Number); const b = min.split('.').map(Number); for (let i = 0; i < 3; i++) { if ((a[i] || 0) !== b[i]) return (a[i] || 0) > b[i]; } return true; };
// The two Epson scanners StudioCore supports (desktop SCANNER_IMAGES / getDisplayName / driver buttons).
const MODELS = { V39II: { name: 'Epson Perfection V39 II', img: '/scanners/39ii.png' }, V19II: { name: 'Epson Perfection V19 II', img: '/scanners/19ii.png' } };
const modelOf = (s) => s.model_hint || (/v\s*19/i.test(s.name || '') ? 'V19II' : /v\s*39/i.test(s.name || '') ? 'V39II' : null);
const SCAN_AI_ON = false;
const AI_FEATURES = [
  { key: 'upscale', label: 'scan.ai.upscale', hint: 'scan.ai.upscaleHint', pricingKey: 'ai_upscale' },
  { key: 'remove_bg', label: 'scan.ai.removeBg', hint: 'scan.ai.removeBgHint', pricingKey: 'ai_remove_bg' },
];
const stamp = () => new Date().toLocaleString(i18n.language).replace(/[/:]/g, '-');

async function fetchScan(key) {
  const s = getSession();
  const res = await fetch(`${API_BASE}/bridge/files/${key.split('/').map(encodeURIComponent).join('/')}`, { headers: { Authorization: `Bearer ${s && s.token}` } });
  if (!res.ok) throw new Error(i18n.t('bv:scan.loadFailed', { status: res.status }));
  return res.blob();
}
const toDataUrl = (blob) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });

function DriverInstall({ device, notify }) {
  const { t } = useTranslation('bv');
  const [busy, setBusy] = useState(null);
  const [pct, setPct] = useState(null);
  const [msg, setMsg] = useState(null);
  const install = async (model) => {
    setBusy(model); setPct(0); setMsg(null);
    const r = await runCommand(device.id, 'scanner-driver', { action: 'download', model }, {
      requireOnline: true, timeoutMs: 15 * 60000, onEvent: (e) => { if (e.ev === 'progress' && e.pct != null) setPct(Math.round(e.pct)); },
    });
    if (!r.ok || !r.result.filePath) { setBusy(null); setPct(null); setMsg(r.error || t('scan.driver.downloadFailed')); return; }
    const l = await runCommand(device.id, 'scanner-driver', { action: 'launch', filePath: r.result.filePath }, { requireOnline: true, timeoutMs: 120000 });
    setBusy(null); setPct(null);
    if (l.ok) { setMsg(t('scan.driver.opened', { model: MODELS[model].name, computer: device.name })); notify(t('scan.driver.openedToast')); }
    else setMsg(l.error || t('scan.driver.launchFailed'));
  };
  return (
    <div className="bv-scan__driver" data-testid="driver-section">
      <div className="bv-label">{t('scan.driver.title')}</div>
      <div className="bv-scan__driver-btns">
        {['V19II', 'V39II'].map((m) => (
          <Button key={m} size="sm" icon="down" disabled={!!busy} onClick={() => install(m)} data-testid={`driver-${m}`}>
            {busy === m ? t('common.downloading') : t('scan.driver.button', { model: m.replace('II', ' II') })}
          </Button>
        ))}
      </div>
      {busy && pct != null && (
        <div className="bv-scan__bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${pct}%` }} /></div>
      )}
      {busy && pct != null && <p className="bv-small bv-muted">{t('scan.driver.pct', { pct, computer: device.name })}</p>}
      {msg && <p className="bv-small bv-muted" role="status">{msg}</p>}
    </div>
  );
}

function ScanResult({ s, patch, remove, notify }) {
  const { t } = useTranslation('bv');
  const navigate = useNavigate();
  const { balance, setBalance, openCredits } = useWallet();
  const [pick, setPick] = useState(() => new Set(['upscale']));
  const [cost, setCost] = useState(null);
  useEffect(() => {
    getPricing().then((r) => {
      const m = r.ok && r.data && r.data.data ? r.data.data.pricingMap || {} : {};
      setCost({ upscale: m.ai_upscale, remove_bg: m.ai_remove_bg });
    });
  }, []);
  const first = AI_FEATURES.find((f) => pick.has(f.key));
  const price = first && cost ? cost[first.key] : null;
  const short = price != null && balance != null && balance < price;

  const enhance = async () => {
    if (short) { openCredits(); return; }
    patch(s.id, { enhancing: true, enhanceError: null });
    const features = AI_FEATURES.filter((f) => pick.has(f.key)).map((f) => f.key);
    const sess = getSession();
    let res; let j = {};
    try {
      res = await fetch(`${API_BASE}/design/enhance?features=${features.join(',')}&w=${s.w || ''}&h=${s.h || ''}`, {
        method: 'POST', headers: { Authorization: `Bearer ${sess && sess.token}`, 'Content-Type': s.original.type || 'image/png' }, body: s.original,
      });
      j = await res.json().catch(() => ({}));
    } catch { res = { ok: false, status: 0 }; }
    if (!res.ok || !j.success) {
      if (res.status === 402) { patch(s.id, { enhancing: false, enhanceError: t('scan.ai.needCredits') }); openCredits(); return; }
      const m = res.status === 503 ? t('scan.ai.off') : (j.message || t('scan.ai.failed'));
      patch(s.id, { enhancing: false, enhanceError: m });
      return;
    }
    const d = j.data;
    try {
      const blob = await fetch(d.imageUrl).then((r) => { if (!r.ok) throw new Error(); return r.blob(); });
      const file = new File([blob], s.file.name.replace(/\.png$/, ' (AI).png'), { type: blob.type || 'image/png' });
      patch(s.id, { enhancing: false, file, url: URL.createObjectURL(blob), w: d.width || s.w, h: d.height || s.h, enhanced: d.features, key: null, saved: false });
      if (d.newBalance != null) setBalance(d.newBalance);
      notify(t('scan.ai.charged', { credits: t('common.credits', { count: d.creditsCharged }), balance: d.newBalance }));
    } catch { patch(s.id, { enhancing: false, enhanceError: t('scan.ai.loadFailed') }); }
  };
  const reset = () => patch(s.id, { file: s.originalFile, url: s.originalUrl, w: s.ow, h: s.oh, enhanced: null, key: s.originalKey, saved: false });
  const print = () => { const f = s.file; if (s.key) f.__bvKey = s.key; handToPrint([f]); navigate('/print'); };
  // Desktop handleEdit: the scan goes in as an embedded data URL so the design keeps it.
  const edit = async () => {
    window.pendingDesignLoad = { designId: null, json: null, name: s.file.name.replace(/\.png$/, ''), imageUrl: await toDataUrl(s.file) };
    navigate('/design');
  };
  const save = async () => {
    patch(s.id, { saving: true });
    const thumb = await makeThumbnail(s.file); // not `t`: that is the translator, used for the saved toast below
    const r = await saveDesign({
      name: s.file.name.replace(/\.png$/, ''), source: 'upload', width: thumb.width, height: thumb.height,
      // Same metadata keys as desktop ScanToPrint (minus its local file paths).
      metadata: { source: 'scanToPrint', platform: 'Scan', dpi: s.dpi || null, width_px: s.w || null, height_px: s.h || null, scanner_name: s.scanner || null, ai_enhanced: !!s.enhanced, ai_features_used: s.enhanced || [] },
    }, { thumbnail: thumb.file, file: s.file, ...thumb.previews });
    patch(s.id, { saving: false, saved: r.ok, saveError: r.ok ? null : r.error });
    if (r.ok) notify(t('scan.savedToast'));
  };
  const download = () => { const a = document.createElement('a'); a.href = s.url; a.download = s.file.name; a.click(); };
  const busy = s.enhancing;
  return (
    <Card className="bv-scan__item" data-testid="scan-result">
      <div className="bv-scan__pic">
        <img src={s.url} alt={t('scan.scanAlt')} />
        {s.enhanced && <Tag color="purple" className="bv-scan__ai" data-testid="ai-badge">{t('scan.ai.badge')}</Tag>}
        {busy && <div className="bv-scan__busy"><Spinner /> {t('scan.ai.enhancing')}</div>}
      </div>
      <div className="bv-scan__info">
        <b>{s.file.name}</b>
        <span className="bv-small bv-muted" data-testid="scan-meta">{s.w} × {s.h} px{s.dpi ? ` · ${s.dpi} dpi` : ''}{s.scanner ? ` · ${s.scanner}` : ''}</span>
        {/* AI Enhance (upscale / remove background) taken off 2026-10-21 (owner): its provider (fal) is not covered by the
            owner's Cloudflare AI Gateway credits. Flip SCAN_AI_ON to bring it back. */}
        {SCAN_AI_ON && <div className="bv-scan__ai-panel" data-testid="ai-panel">
          <div className="bv-label">{t('scan.ai.title')}</div>
          {AI_FEATURES.map((f) => (
            <label key={f.key} className={`bv-scan__feat${pick.has(f.key) ? ' is-on' : ''}${s.enhanced && s.enhanced.includes(f.key) ? ' is-applied' : ''}`}>
              <input type="checkbox" checked={pick.has(f.key)} disabled={busy || !!s.enhanced}
                onChange={() => setPick((p) => { const n = new Set(p); n.has(f.key) ? n.delete(f.key) : n.add(f.key); return n; })} data-testid={`feat-${f.key}`} />
              <span><b>{t(f.label)}</b><span className="bv-small bv-muted">{s.enhanced && s.enhanced.includes(f.key) ? t('scan.ai.applied') : t(f.hint)}</span></span>
            </label>
          ))}
          {s.enhanced ? (
            <Button icon="undo" onClick={reset} disabled={busy} data-testid="reset-original">{t('scan.ai.reset')}</Button>
          ) : (
            <Button variant="purple" icon="sparkle" disabled={busy || !pick.size} onClick={enhance} data-testid="enhance-button">
              {busy ? t('scan.ai.enhancing') : short ? t('scan.ai.notEnough') : `${t('scan.ai.title')}${price != null ? ` · ${t('common.credits', { count: price })}` : ''}`}
            </Button>
          )}
          <p className="bv-small bv-muted">{t('scan.ai.note')}</p>
          {s.enhanceError && <p className="bv-error-text" role="alert">{s.enhanceError}</p>}
        </div>}
        <div className="bv-scan__actions">
          <Button variant="coral" icon="print" disabled={busy} onClick={print}>{t('print.print')}</Button>
          <Button variant="blue" icon="design" disabled={busy} onClick={edit} data-testid="scan-edit">{t('library.detail.edit')}</Button>
          <Button icon={s.saved ? 'check' : 'library'} disabled={busy || s.saving || s.saved} onClick={save}>{s.saved ? t('common.saved') : s.saving ? t('common.saving') : t('common.saveToLibrary')}</Button>
          <Button icon="down" disabled={busy} onClick={download}>{t('common.download')}</Button>
          <Button icon="trash" disabled={busy} onClick={() => remove(s.id)}>{t('common.remove')}</Button>
        </div>
        {s.saveError && <p className="bv-error-text">{s.saveError}</p>}
        {s.saved && <Tag color="green">{t('common.inLibrary')}</Tag>}
      </div>
    </Card>
  );
}

export default function ScanPage() {
  const { t } = useTranslation('bv');
  const { devices, refresh: refreshDevices } = useDevices(15000);
  const { local } = useLocalComputer(devices, refreshDevices, 15000);   // the Helper on this computer wins (local check), claimed for this user when new
  const store = usePrintStore();
  const notify = (t, tone = 'green') => store && store.notify(t, tone);
  const [deviceId, setDeviceId] = useState(null);
  const windows = useMemo(() => (devices || []).filter((d) => d.os === 'win32'), [devices]);
  const device = (local && windows.find((d) => d.id === local.deviceId)) || windows.find((d) => d.id === deviceId) || windows.find((d) => d.online) || windows[0] || null;
  const [scanners, setScanners] = useState(null);
  const [scanner, setScanner] = useState('');
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [phase, setPhase] = useState(null);   // {sending, mb} while the Helper sends the scan up
  const [scans, setScans] = useState([]);
  const [look, setLook] = useState(0);
  const tooOld = device && !atLeast(device.engineVersion, '0.1.7');

  useEffect(() => {
    if (!device || !device.online) { setScanners(null); return; }
    let gone = false;
    setScanners(null); setErr(null);
    runCommand(device.id, 'scan-discover', {}, { requireOnline: true, timeoutMs: 90000 }).then((r) => {
      if (gone) return;
      const list = r.ok ? (r.result.scanners || []) : [];
      if (!r.ok) setErr(r.error);
      // The Epson the desktop app auto-connects to goes first.
      list.sort((a, b) => (/epson/i.test(b.name) ? 1 : 0) - (/epson/i.test(a.name) ? 1 : 0));
      setScanners(list);
      setScanner((s) => (list.some((x) => x.device_id === s) ? s : (list[0] && list[0].device_id) || ''));
    });
    return () => { gone = true; };
  }, [device && device.id, device && device.online, look]); // eslint-disable-line react-hooks/exhaustive-deps

  const scan = async (deviceIdOfScanner) => {
    const sid = deviceIdOfScanner || scanner;
    setScanner(sid); setBusy('scan'); setErr(null); setPhase(null);
    const r = await runCommand(device.id, 'scan', sid ? { deviceId: sid } : {}, { requireOnline: true, timeoutMs: 1500000,
      onEvent: (e) => { if (e.ev === 'progress' && e.stage === 'uploading') setPhase({ sending: true, mb: e.bytes ? Math.round(e.bytes / 1048576) : null }); } });
    if (!r.ok || !r.result.fileKey) { setBusy(null); setPhase(null); setErr(r.error || t('scan.notReached')); return; }
    try {
      const blob = await fetchScan(r.result.fileKey);
      const file = new File([blob], `${t('scan.scanAlt')} ${stamp()}.${/jpeg/.test(blob.type || '') ? 'jpg' : 'png'}`, { type: blob.type || 'image/png' });
      const url = URL.createObjectURL(blob);
      setScans((s) => [{
        id: r.commandId, key: r.result.fileKey, file, url, w: r.result.width_px, h: r.result.height_px, dpi: r.result.dpi, scanner: r.result.scanner_name,
        original: blob, originalFile: file, originalUrl: url, originalKey: r.result.fileKey, ow: r.result.width_px, oh: r.result.height_px,
      }, ...s]);
    } catch (e) { setErr(String(e.message || e)); }
    setBusy(null); setPhase(null);
  };
  const patch = useCallback((id, p) => setScans((s) => s.map((x) => (x.id === id ? { ...x, ...p } : x))), []);
  const remove = useCallback((id) => setScans((x) => x.filter((y) => y.id !== id)), []);

  const noWindows = devices && windows.length === 0;
  const offline = device && !device.online;
  return (
    <div className="bv-content bv-scan">
      <TopBar title={t('scan.title')} subtitle={t('scan.subtitle')} />
      {!devices ? <Card><Spinner /></Card> : noWindows ? (
        <Card><EmptyState icon="scan" title={t('scan.needWindows.title')}
          action={<Button as={Link} to="/settings#computers" variant="blue" icon="link">{t('print.noComputer.action')}</Button>}>
          {t('scan.needWindows.body')}
        </EmptyState></Card>
      ) : (
        <div className="bv-scan__grid">
          <Card className="bv-scan__panel" data-testid="scan-panel" data-tour="scan-panel">
            <IconTile icon="scan" color="coral" size={64} iconSize={30} />
            <div className="bv-label">{t('print.computer')}</div>
            <Select aria-label={t('print.computer')} value={device ? device.id : ''} onChange={(e) => setDeviceId(e.target.value)}
              options={windows.map((d) => ({ value: d.id, label: `${d.name}${d.online ? '' : ` (${t('common.offline')})`}` }))} />
            <div className="bv-label">{t('scan.scanner')}</div>
            {offline ? <p className="bv-small bv-muted">{t('scan.deviceOffline', { name: device.name })}</p>
              : !scanners ? <p className="bv-small bv-muted"><Spinner label={t('scan.looking')} /> {t('scan.looking')}…</p>
                : scanners.length === 0 ? (
                  <div className="bv-scan__none" data-testid="no-scanner">
                    <b>{t('scan.none.title')}</b>
                    <p className="bv-small bv-muted">{t('scan.none.models')}</p>
                    <p className="bv-scan__usb"><Icon name="link" size={18} /> {t('scan.none.usb', { name: device.name })}</p>
                  </div>
                ) : (
                  <div className="bv-scan__devices" data-testid="scanner-cards">
                    {scanners.map((s) => {
                      const m = MODELS[modelOf(s)];
                      return (
                        <button key={s.device_id} type="button" className={`bv-scan__device${scanner === s.device_id ? ' is-on' : ''}`} aria-pressed={scanner === s.device_id} onClick={() => setScanner(s.device_id)}>
                          <img src={(m || MODELS.V39II).img} alt="" draggable={false} />
                          <span>{m ? m.name : s.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
            {device && device.online && (
              <Button size="sm" icon="refresh" disabled={!scanners || !!busy} onClick={() => setLook((n) => n + 1)} data-testid="redetect">{scanners && scanners.length ? t('scan.redetect') : t('scan.lookAgain')}</Button>
            )}
            {tooOld && <p className="bv-error-text">{t('scan.tooOld', { name: device.name })}</p>}
            <p className="bv-small bv-muted">{t('scan.faceDown')}</p>
            <Button variant="coral" size="big" block icon="scan" disabled={!device || !device.online || !scanners || !scanners.length || !!busy || tooOld} onClick={() => scan()} data-testid="scan-button">
              {busy === 'scan' ? t('scan.scanning') : t('scan.scanAlt')}
            </Button>
            {err && (
              <div className="bv-scan__err" role="alert">
                <p className="bv-error-text">{err}</p>
                {scanners && scanners.length > 0 && <Button size="sm" icon="refresh" onClick={() => scan()} disabled={!!busy} data-testid="scan-retry">{t('common.tryAgain')}</Button>}
              </div>
            )}
            {device && device.online && scanners && scanners.length === 0 && <DriverInstall device={device} notify={notify} />}
          </Card>
          <div className="bv-scan__results">
            {busy === 'scan' && (
              <Card className="bv-scan__scanning" data-testid="scanning"><div className="bv-scan__glass"><span /></div><b>{phase && phase.sending ? t('scan.sending') : t('scan.scanning')}</b><span className="bv-small bv-muted">{phase && phase.sending ? (phase.mb ? t('scan.sendingMb', { mb: phase.mb }) : t('scan.sendingSub')) : t('scan.takes20')}</span></Card>
            )}
            {scans.length === 0 && busy !== 'scan' ? (
              <Card><EmptyState icon="image" title={t('scan.empty.title')}>{t('scan.empty.body')}</EmptyState></Card>
            ) : scans.map((s) => <ScanResult key={s.id} s={s} patch={patch} remove={remove} notify={notify} />)}
          </div>
        </div>
      )}
      <p className="bv-small bv-muted"><Icon name="resources" size={14} /> {t('scan.trouble')} <Link to="/resources">{t('nav.resources')}</Link>.</p>
    </div>
  );
}
